import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get or create session ID for consistent variant assignment
const getABSessionId = () => {
  let sessionId = localStorage.getItem('ab_session_id');
  if (!sessionId) {
    sessionId = `ab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('ab_session_id', sessionId);
  }
  return sessionId;
};

export const useABTest = (testId) => {
  const [variant, setVariant] = useState('A');
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const sessionId = getABSessionId();

  // Fetch variant configuration
  useEffect(() => {
    if (!testId) return;

    const fetchVariant = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/ab-test/config/${testId}?session_id=${sessionId}`
        );
        setVariant(response.data.variant);
        setConfig(response.data.config || {});
        
        // Track view event
        await axios.post(`${BACKEND_URL}/api/ab-test/track`, {
          test_id: testId,
          variant: response.data.variant,
          event_type: 'view',
          page: window.location.pathname,
          session_id: sessionId
        });
      } catch (error) {
        console.debug('A/B test fetch error:', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVariant();
  }, [testId, sessionId]);

  // Track click event
  const trackClick = useCallback(async () => {
    if (!testId) return;

    try {
      await axios.post(`${BACKEND_URL}/api/ab-test/track`, {
        test_id: testId,
        variant,
        event_type: 'click',
        page: window.location.pathname,
        session_id: sessionId
      });
    } catch (error) {
      console.debug('A/B track click error:', error.message);
    }
  }, [testId, variant, sessionId]);

  return { variant, config, loading, trackClick };
};

// A/B Test enabled CTA Button component
export const ABTestButton = ({ 
  testId, 
  defaultText, 
  defaultColor = 'red',
  onClick,
  className = '',
  children,
  ...props 
}) => {
  const { config, loading, trackClick } = useABTest(testId);

  const handleClick = async (e) => {
    await trackClick();
    if (onClick) onClick(e);
  };

  const text = config.text || defaultText || children;
  const colorClass = getColorClass(config.color || defaultColor);

  if (loading) {
    return (
      <button 
        className={`${className} ${colorClass} opacity-90`}
        {...props}
      >
        {defaultText || children}
      </button>
    );
  }

  return (
    <button 
      onClick={handleClick}
      className={`${className} ${colorClass}`}
      data-ab-test={testId}
      data-ab-variant={config.variant}
      {...props}
    >
      {text}
    </button>
  );
};

// Helper to get Tailwind color classes
const getColorClass = (color) => {
  const colorMap = {
    red: 'bg-gradient-to-r from-[#ef4444] to-[#dc2626] hover:from-[#dc2626] hover:to-[#b91c1c]',
    green: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
    blue: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
    orange: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
  };
  return colorMap[color] || colorMap.red;
};

export default useABTest;
